import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Plus,
  Trash2,
  Power,
  X,
  Calendar,
  Tag,
  Percent,
  Banknote,
  Users,
  Edit2,
  Copy,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Info,
} from "lucide-react";
import { useToast } from "../../context/ToastContext";
import BaseApi from "../../services/base-api";
import { useTranslation } from "react-i18next";
import { LocalizedText } from "../../types/LocalizedText";
import { useAuth } from "../../context/AuthContext";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { Tooltip } from "@mui/material";
import { Money } from "@mui/icons-material";
import LoadingComponent from "../../components/LoadingComponent";
import { useDebounce } from "../../hooks/useDebounce";
import Pagination from "../../components/Pagination";
import { useCurrency } from "../../components/Currency/CurrencyContext";

interface Coupon {
  id: string;
  code: string;
  name?: LocalizedText;
  description?: LocalizedText;
  type: "percentage" | "fixed";
  discountValue: number;
  minPurchaseAmount: number;
  maxDiscountAmount: number;
  usageLimit: number;
  usageLimitPerUser: number;
  applicableToCategories: string[];
  applicableToProducts: string[];
  excludedCategories: string[];
  excludedProducts: string[];
  applicableToUsers: string[];
  applicableToNewUsersOnly: boolean;
  buyXGetYConfig: any | undefined | null;
  startsAt: string | Date;
  expiresAt: string | Date;
  internalNotes: string;
  minOrder?: number;
  maxDiscount?: number;
  isActive: boolean;
}

const CouponsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { success, err } = useToast();
  const { currency } = useCurrency();
  const { permissions } = useAuth();
  // Check if user has permission to create or edit products
  const canCreate = hasPermission(permissions, PERMISSIONS.CREATE_COUPONS);
  // User can add category if they have either create or edit permission

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const latestRequestRef = useRef(0);
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [filteredCoupons, setFilteredCoupons] = useState<Coupon[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [filterType, setFilterType] = useState<"all" | "percentage" | "fixed">(
    "all",
  );
  const [showFilters, setShowFilters] = useState(false);
  const [expandedCoupon, setExpandedCoupon] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [form, setForm] = useState<{
    code: string;
    name: LocalizedText;
    description: LocalizedText;
    type: "percentage" | "fixed";
    discountValue: number;
    minPurchaseAmount: number;
    maxDiscountAmount: number;
    usageLimit: number;
    usageLimitPerUser: number;
    applicableToCategories: string[];
    applicableToProducts: string[];
    excludedCategories: string[];
    excludedProducts: string[];
    applicableToUsers: string[];
    applicableToNewUsersOnly: boolean;
    buyXGetYConfig: any;
    startsAt: string;
    expiresAt: string;
    internalNotes: string;
  }>({
    code: "",
    name: { en: "", ar: "" },
    description: { en: "", ar: "" },
    type: "percentage",
    discountValue: 10,
    minPurchaseAmount: 100,
    maxDiscountAmount: 50,
    usageLimit: 500,
    usageLimitPerUser: 1,
    applicableToCategories: [],
    applicableToProducts: [],
    excludedCategories: [],
    excludedProducts: [],
    applicableToUsers: [],
    applicableToNewUsersOnly: false,
    buyXGetYConfig: null,
    startsAt: new Date().toISOString().split("T")[0],
    expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    internalNotes: "",
  });

  const debouncedSearch = useDebounce(searchTerm.trim());
  const [apiTotalItems, setApiTotalItems] = useState(0);

  // ================= FETCH =================
  const loadCoupons = useCallback(async () => {
    const requestId = ++latestRequestRef.current;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
      });

      // Add search filter
      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }

      // Add status filter
      if (filterStatus !== "all") {
        params.append("status", filterStatus);
      }

      // Add type filter
      if (filterType !== "all") {
        params.append("type", filterType);
      }

      const { data, pagination } = await BaseApi.get(
        `/coupons?${params.toString()}`,
      );
      if (requestId !== latestRequestRef.current) return;
      if (!Array.isArray(data)) {
        console.error("Expected an array of coupons");
        return;
      }
      setCoupons(data);
      setFilteredCoupons(data);
      setApiTotalItems(pagination?.totalItems || data.length);
    } catch {
      if (requestId !== latestRequestRef.current) return;
      err("Failed to load coupons");
    } finally {
      if (requestId === latestRequestRef.current) {
        setLoading(false);
        setInitialLoading(false);
      }
    }
  }, [
    currentPage,
    itemsPerPage,
    debouncedSearch,
    filterStatus,
    filterType,
    err,
  ]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filterStatus, filterType]);

  // ================= CREATE/UPDATE =================
  const handleSubmit = async () => {
    try {
      if (editingCoupon) {
        const res = await BaseApi.put(`/coupons/${editingCoupon.id}`, form);
        if (res.success) {
          success("Coupon updated successfully");
          resetForm();
          loadCoupons();
        } else {
          err("Couldn't update coupon");
        }
      } else {
        const res = await BaseApi.post("/coupons", form);
        if (res.success) {
          success("Coupon created successfully");
          resetForm();
          loadCoupons();
        } else {
          err("Couldn't create coupon");
        }
      }
    } catch (error) {
      console.error(
        editingCoupon ? "Failed to update coupon" : "Failed to create coupon",
        error,
      );
    }
  };

  // ================= DELETE =================
  const deleteCoupon = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;

    try {
      await BaseApi.delete(`/coupons/${id}`);
      success("Coupon deleted successfully");
      setCoupons((c) => c.filter((x) => x.id !== id));
    } catch (error) {
      console.error("Failed to delete coupon", error);
    }
  };

  // ================= TOGGLE =================
  const toggleCoupon = async (id: string) => {
    try {
      await BaseApi.patch(`/coupons/${id}/toggle`);
      setCoupons((c) =>
        c.map((x) => (x.id === id ? { ...x, isActive: !x.isActive } : x)),
      );
      success("Coupon status updated");
    } catch (error) {
      console.error("Failed to toggle coupon", error);
    }
  };

  // ================= DUPLICATE =================
  const duplicateCoupon = (coupon: Coupon) => {
    setForm({
      code: `${coupon.code}_COPY`,
      name: coupon.name ? { ...coupon.name } : { en: "", ar: "" },
      description: coupon.description
        ? { ...coupon.description }
        : { en: "", ar: "" },
      type: coupon.type,
      discountValue: coupon.discountValue,
      minPurchaseAmount: coupon.minPurchaseAmount,
      maxDiscountAmount: coupon.maxDiscountAmount,
      usageLimit: coupon.usageLimit,
      usageLimitPerUser: coupon.usageLimitPerUser,
      applicableToCategories: [...(coupon.applicableToCategories || [])],
      applicableToProducts: [...(coupon.applicableToProducts || [])],
      excludedCategories: [...(coupon.excludedCategories || [])],
      excludedProducts: [...(coupon.excludedProducts || [])],
      applicableToUsers: [...(coupon.applicableToUsers || [])],
      applicableToNewUsersOnly: coupon.applicableToNewUsersOnly || false,
      buyXGetYConfig: coupon.buyXGetYConfig || null,
      startsAt: new Date().toISOString().split("T")[0],
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      internalNotes: coupon.internalNotes || "",
    });
    setEditingCoupon(null);
    setShowModal(true);
  };

  // ================= EDIT =================
  const editCoupon = (coupon: Coupon) => {
    setForm({
      code: coupon.code,
      name: coupon.name ? { ...coupon.name } : { en: "", ar: "" },
      description: coupon.description
        ? { ...coupon.description }
        : { en: "", ar: "" },
      type: coupon.type,
      discountValue: coupon.discountValue,
      minPurchaseAmount: coupon.minPurchaseAmount,
      maxDiscountAmount: coupon.maxDiscountAmount,
      usageLimit: coupon.usageLimit,
      usageLimitPerUser: coupon.usageLimitPerUser,
      applicableToCategories: [...(coupon.applicableToCategories || [])],
      applicableToProducts: [...(coupon.applicableToProducts || [])],
      excludedCategories: [...(coupon.excludedCategories || [])],
      excludedProducts: [...(coupon.excludedProducts || [])],
      applicableToUsers: [...(coupon.applicableToUsers || [])],
      applicableToNewUsersOnly: coupon.applicableToNewUsersOnly || false,
      buyXGetYConfig: coupon.buyXGetYConfig || null,
      startsAt:
        typeof coupon.startsAt === "string"
          ? coupon.startsAt.split("T")[0]
          : new Date(coupon.startsAt).toISOString().split("T")[0],
      expiresAt:
        typeof coupon.expiresAt === "string"
          ? coupon.expiresAt.split("T")[0]
          : new Date(coupon.expiresAt).toISOString().split("T")[0],
      internalNotes: coupon.internalNotes || "",
    });
    setEditingCoupon(coupon);
    setShowModal(true);
  };

  // ================= RESET FORM =================
  const resetForm = () => {
    setForm({
      code: "",
      name: { en: "", ar: "" },
      description: { en: "", ar: "" },
      type: "percentage",
      discountValue: 10,
      minPurchaseAmount: 100,
      maxDiscountAmount: 50,
      usageLimit: 500,
      usageLimitPerUser: 1,
      applicableToCategories: [],
      applicableToProducts: [],
      excludedCategories: [],
      excludedProducts: [],
      applicableToUsers: [],
      applicableToNewUsersOnly: false,
      buyXGetYConfig: null,
      startsAt: new Date().toISOString().split("T")[0],
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      internalNotes: "",
    });
    setEditingCoupon(null);
    setShowModal(false);
  };

  // ================= FORMAT DATE =================
  const formatDate = (date: string | Date) => {
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // ================= STATS =================
  const stats = {
    total: apiTotalItems, // Use apiTotalItems instead of coupons.length
    active: coupons?.filter((c) => c.isActive).length,
    inactive: coupons?.filter((c) => !c.isActive).length,
    percentage: coupons?.filter((c) => c.type === "percentage").length,
    fixed: coupons?.filter((c) => c.type === "fixed").length,
  };

  // Pagination calculations
  const totalPages = Math.ceil(apiTotalItems / itemsPerPage);

  return (
    <div className="min-h-screen bg-[#f2e6db] py-8 px-4">
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }

        .glass-effect {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .animate-slide-up {
          animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .animate-slide-in {
          animation: slideIn 0.5s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .animate-fade-in {
          animation: fadeIn 0.4s ease-out;
        }

        .animate-scale-in {
          animation: scaleIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .gradient-text {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .coupon-card {
          transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .coupon-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }

        .btn-primary {
          background: linear-gradient(135deg, #1a234b 0%, #020203 100%);
          transition: all 0.3s ease;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(102, 126, 234, 0.4);
        }

        .stat-card {
          position: relative;
          overflow: hidden;
        }

        .stat-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
          transition: left 0.5s;
        }

        .stat-card:hover::before {
          left: 100%;
        }

        input:focus, textarea:focus, select:focus {
          outline: none;
          ring: 2px;
          ring-color: #667eea;
          border-color: #667eea;
        }

        .modal-backdrop {
          animation: fadeIn 0.3s ease-out;
        }

        .modal-content {
          animation: scaleIn 0.4s cubic-bezier(0.16, 1, 0.3, 1);
        }

        .filter-badge {
          transition: all 0.2s ease;
        }

        .filter-badge:hover {
          transform: scale(1.05);
        }

        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }

        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      <div className="mx-auto">
        {/* Header */}
        <div className="animate-slide-up mb-4">
          <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 border border-gray-100">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div className="flex items-center gap-5">
                <div className="relative">
                  <div className="relative bg-gradient-to-br from-gray-700 to-black rounded-2xl p-4">
                    <Tag className="w-8 h-8 sm:w-6 sm:h-6 text-white" />
                  </div>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-black mb-1">
                    {t("Coupons")}
                  </h1>
                  <p className="text-gray-600 text-sm">
                    {t("Create and manage discount codes for your store")}
                  </p>
                </div>
              </div>

              <Tooltip
                title={
                  canCreate
                    ? t("Add Coupon")
                    : t("You don't have permission to add coupon")
                }
                placement="top"
              >
                <button
                  disabled={!canCreate}
                  onClick={() => {
                    resetForm();
                    setShowModal(true);
                  }}
                  className="flex items-center gap-2 bg-black text-white px-6 py-3.5 rounded-xl font-semibold shadow-lg"
                >
                  <Plus className="w-8 h-8" />
                  <span>{t("Create Coupon")}</span>
                </button>
              </Tooltip>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-4 animate-slide-in">
          <div className="stat-card flex items-center justify-between bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-blue-200">
                <Tag className="w-8 h-8 text-blue-600" />
              </div>
              <span className="text-blue-500 text-lg font-medium tracking-wide">
                {t("Total")}
              </span>
            </div>
            <span className="text-3xl font-extrabold text-blue-900">
              {stats.total}
            </span>
          </div>

          <div className="stat-card flex items-center justify-between bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-green-100">
                <Power className="w-8 h-8 text-green-600" />
              </div>
              <span className="text-green-500 text-lg font-medium tracking-wide">
                {t("Active")}
              </span>
            </div>
            <span className="text-3xl font-extrabold text-green-600">
              {stats.active}
            </span>
          </div>

          <div className="stat-card flex items-center justify-between bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-red-100">
                <Power className="w-8 h-8 text-red-600" />
              </div>
              <span className="text-red-500 text-lg font-medium tracking-wide">
                {t("Inactive")}
              </span>
            </div>
            <span className="text-3xl font-extrabold text-red-600">
              {stats.inactive}
            </span>
          </div>

          <div className="stat-card flex items-center justify-between bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-purple-100">
                <Percent className="w-8 h-8 text-purple-600" />
              </div>
              <span className="text-purple-500 text-lg font-medium tracking-wide">
                {t("Percentage")}
              </span>
            </div>
            <span className="text-3xl font-extrabold text-purple-600">
              {stats.percentage}
            </span>
          </div>

          <div className="stat-card flex items-center justify-between bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-amber-100">
                {currency.symbol}
              </div>
              <span className="text-amber-500 text-lg font-medium tracking-wide">
                {t("Fixed")}
              </span>
            </div>
            <span className="text-3xl font-extrabold text-amber-600">
              {stats.fixed}
            </span>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-4 border border-gray-100 animate-fade-in">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 text-gray-400" />
              <input
                type="text"
                placeholder="Search coupons by code or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:border-violet-500 transition-colors"
              />
            </div>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors font-medium"
            >
              <Filter className="w-6 h-6" />
              <span>Filters</span>
              {showFilters ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-3 animate-slide-in">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  {t("Status")}:
                </span>
                <button
                  onClick={() => setFilterStatus("all")}
                  className={`filter-badge px-4 py-2 rounded-lg text-sm font-medium ${
                    filterStatus === "all"
                      ? "bg-gray-600 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {t("All")}
                </button>
                <button
                  onClick={() => setFilterStatus("active")}
                  className={`filter-badge px-4 py-2 rounded-lg text-sm font-medium ${
                    filterStatus === "active"
                      ? "bg-green-600 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {t("Active")}
                </button>
                <button
                  onClick={() => setFilterStatus("inactive")}
                  className={`filter-badge px-4 py-2 rounded-lg text-sm font-medium ${
                    filterStatus === "inactive"
                      ? "bg-red-600 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {t("Inactive")}
                </button>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  {t("Type")}:
                </span>
                <button
                  onClick={() => setFilterType("all")}
                  className={`filter-badge px-4 py-2 rounded-lg text-sm font-medium ${
                    filterType === "all"
                      ? "bg-gray-600 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {t("All")}
                </button>
                <button
                  onClick={() => setFilterType("percentage")}
                  className={`filter-badge px-4 py-2 rounded-lg text-sm font-medium ${
                    filterType === "percentage"
                      ? "bg-purple-600 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {t("Percentage")}
                </button>
                <button
                  onClick={() => setFilterType("fixed")}
                  className={`filter-badge px-4 py-2 rounded-lg text-sm font-medium ${
                    filterType === "fixed"
                      ? "bg-amber-600 text-white"
                      : "bg-gray-100 text-gray-700"
                  }`}
                >
                  {t("Fixed Amount")}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Coupons Grid */}
        <div
          className={
            "grid gap-4" +
            (loading && !initialLoading
              ? " opacity-50 pointer-events-none transition-opacity"
              : "")
          }
        >
          {initialLoading ? (
            <LoadingComponent title={t("Coupons")} />
          ) : filteredCoupons?.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center shadow-lg">
              <Tag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 text-lg font-medium">
                {debouncedSearch || filterStatus !== "all" || filterType !== "all"
                  ? t("No coupons match your filters")
                  : t("No coupons yet. Create your first one")}
              </p>
            </div>
          ) : (
            filteredCoupons.map((coupon, index) => (
              <div
                key={coupon.id}
                className="coupon-card bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <code className="text-2xl font-bold text-gray-900 bg-gray-100 px-4 py-2 rounded-lg">
                          {coupon.code}
                        </code>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-bold ${
                            coupon.isActive
                              ? "bg-green-100 text-green-700"
                              : "bg-red-100 text-red-600"
                          }`}
                        >
                          {coupon.isActive ? "Active" : "Inactive"}
                        </span>
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-bold ${
                            coupon.type === "percentage"
                              ? "bg-gray-100 text-gray-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {coupon.type === "percentage"
                            ? t("Percentage")
                            : t("Fixed Amount")}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-1">
                        {coupon.name && (coupon.name.en || coupon.name.ar)
                          ? (i18n.language === "ar"
                              ? coupon.name.ar
                              : coupon.name.en) ||
                            coupon.name.en ||
                            coupon.name.ar
                          : coupon.code}
                      </h3>
                      {coupon.description &&
                        (coupon.description.en || coupon.description.ar) && (
                          <p className="text-gray-600 text-sm">
                            {(i18n.language === "ar"
                              ? coupon.description.ar
                              : coupon.description.en) ||
                              coupon.description.en ||
                              coupon.description.ar}
                          </p>
                        )}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => toggleCoupon(coupon.id)}
                        className={`p-2.5 rounded-lg transition-all ${
                          coupon.isActive
                            ? "bg-green-100 text-green-700 hover:bg-green-200"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                        title="Toggle Status"
                      >
                        <Power className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => duplicateCoupon(coupon)}
                        className="p-2.5 rounded-lg bg-blue-200 text-blue-700 hover:bg-blue-400 transition-all"
                        title="Duplicate"
                      >
                        <Copy className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => editCoupon(coupon)}
                        className="p-2.5 rounded-lg bg-violet-100 text-violet-700 hover:bg-violet-200 transition-all"
                        title="Edit"
                      >
                        <Edit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => deleteCoupon(coupon.id)}
                        className="p-2.5 rounded-lg bg-red-100 text-red-700 hover:bg-red-200 transition-all"
                        title="Delete"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Coupon Details Grid */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div className="bg-gradient-to-br from-gray-50 to-purple-50 rounded-xl p-4 border border-violet-100">
                      <div className="flex items-center gap-2 mb-1">
                        {coupon.type === "percentage" ? (
                          <Percent className="w-4 h-4 text-violet-600" />
                        ) : (
                          <Money className="w-4 h-4 text-violet-600" />
                        )}
                        <span className="text-xs font-medium text-gray-600">
                          {t("Discount")}
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-violet-600">
                        {coupon.type === "percentage"
                          ? `${coupon.discountValue}%`
                          : `${coupon.discountValue}`}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4 border border-blue-300">
                      <div className="flex items-center gap-2 mb-1">
                        <Money className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-medium text-gray-600">
                          Min Purchase
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-blue-600">
                        {coupon.minPurchaseAmount}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-100">
                      <div className="flex items-center gap-2 mb-1">
                        <Banknote className="w-4 h-4 text-amber-600" />
                        <span className="text-xs font-medium text-gray-600">
                          Max Discount
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-amber-600">
                        {coupon.maxDiscountAmount}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4 text-green-600" />
                        <span className="text-xs font-medium text-gray-600">
                          Usage Limit
                        </span>
                      </div>
                      <p className="text-2xl font-bold text-green-600">
                        {coupon.usageLimit}
                      </p>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>Starts: {formatDate(coupon.startsAt)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>Expires: {formatDate(coupon.expiresAt)}</span>
                    </div>
                  </div>

                  {/* Expand/Collapse Button */}
                  <button
                    onClick={() =>
                      setExpandedCoupon(
                        expandedCoupon === coupon.id ? null : coupon.id,
                      )
                    }
                    className="w-full flex items-center justify-center gap-2 py-2 text-violet-600 hover:text-violet-700 font-medium text-sm transition-colors"
                  >
                    <Info className="w-4 h-4" />
                    <span>
                      {expandedCoupon === coupon.id
                        ? "Hide Details"
                        : "Show More Details"}
                    </span>
                    {expandedCoupon === coupon.id ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>

                  {/* Expanded Details */}
                  {expandedCoupon === coupon.id && (
                    <div className="mt-4 pt-4 border-t border-gray-100 space-y-3 animate-slide-in">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-gray-50 rounded-lg p-3">
                          <span className="text-xs font-medium text-gray-600 block mb-1">
                            Per User Limit
                          </span>
                          <span className="text-lg font-bold text-gray-900">
                            {coupon.usageLimitPerUser}
                          </span>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3">
                          <span className="text-xs font-medium text-gray-600 block mb-1">
                            New Users Only
                          </span>
                          <span
                            className={`text-lg font-bold ${coupon.applicableToNewUsersOnly ? "text-green-600" : "text-gray-400"}`}
                          >
                            {coupon.applicableToNewUsersOnly ? "Yes" : "No"}
                          </span>
                        </div>
                      </div>

                      {coupon.internalNotes && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                          <span className="text-xs font-medium text-gray-600 block mb-1">
                            Internal Notes
                          </span>
                          <p className="text-sm text-gray-700">
                            {coupon.internalNotes}
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {coupon.applicableToCategories.length > 0 && (
                          <div className="bg-[#0d223f] border border-[#0d223f] rounded-lg p-3">
                            <span className="text-xs font-medium text-gray-600 block mb-2">
                              Applicable Categories
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {coupon.applicableToCategories.map((cat, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs"
                                >
                                  {cat}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {coupon.applicableToProducts.length > 0 && (
                          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                            <span className="text-xs font-medium text-gray-600 block mb-2">
                              {t("Applicable Products")}
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {coupon.applicableToProducts.map((prod, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs"
                                >
                                  {prod}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-backdrop fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="modal-content bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-700 to-black px-8 py-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">
                {editingCoupon ? "Edit Coupon" : "Create New Coupon"}
              </h2>
              <button
                onClick={resetForm}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-8 h-8 text-white" />
              </button>
            </div>

            {/* Modal Body */}
            <div
              className="p-8 overflow-y-auto scrollbar-hide"
              style={{ maxHeight: "calc(90vh - 180px)" }}
            >
              <div className="space-y-6">
                {/* Basic Info Section */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Tag className="w-6 h-6 text-violet-600" />
                    Basic Information
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Coupon Code *
                      </label>
                      <input
                        type="text"
                        placeholder="e.g., SUMMER2026"
                        value={form.code}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            code: e.target.value.toUpperCase(),
                          })
                        }
                        className="w-full border-2 border-gray-200 px-4 py-3 rounded-xl focus:border-violet-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Discount Type *
                      </label>
                      <select
                        value={form.type}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            type: e.target.value as "percentage" | "fixed",
                          })
                        }
                        className="w-full border-2 border-gray-200 px-4 py-3 rounded-xl focus:border-violet-500 transition-colors"
                      >
                        <option value="percentage">
                          {t("Percentage Discount")}
                        </option>
                        <option value="fixed">
                          {t("Fixed Amount Discount")}
                        </option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        {t("Name (English)")}
                      </label>
                      <input
                        type="text"
                        placeholder="Coupon name in English"
                        value={form.name.en}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            name: { ...form.name, en: e.target.value },
                          })
                        }
                        className="w-full border-2 border-gray-200 px-4 py-3 rounded-xl focus:border-violet-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Name (Arabic)
                      </label>
                      <input
                        type="text"
                        placeholder="اسم القسيمة بالعربية"
                        value={form.name.ar}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            name: { ...form.name, ar: e.target.value },
                          })
                        }
                        className="w-full border-2 border-gray-200 px-4 py-3 rounded-xl focus:border-violet-500 transition-colors"
                        dir="rtl"
                      />
                    </div>

                    <div className="lg:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Description (English)
                      </label>
                      <textarea
                        placeholder="Describe this coupon in English"
                        value={form.description.en}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            description: {
                              ...form.description,
                              en: e.target.value,
                            },
                          })
                        }
                        rows={2}
                        className="w-full border-2 border-gray-200 px-4 py-3 rounded-xl focus:border-violet-500 transition-colors resize-none"
                      />
                    </div>

                    <div className="lg:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Description (Arabic)
                      </label>
                      <textarea
                        placeholder="وصف القسيمة بالعربية"
                        value={form.description.ar}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            description: {
                              ...form.description,
                              ar: e.target.value,
                            },
                          })
                        }
                        rows={2}
                        className="w-full border-2 border-gray-200 px-4 py-3 rounded-xl focus:border-violet-500 transition-colors resize-none"
                        dir="rtl"
                      />
                    </div>
                  </div>
                </div>

                {/* Discount Settings */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Percent className="w-6 h-6 text-violet-600" />
                    Discount Settings
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Discount Value *
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          placeholder="10"
                          value={form.discountValue}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              discountValue: Number(e.target.value),
                            })
                          }
                          className="w-full border-2 border-gray-200 px-4 py-3 rounded-xl focus:border-violet-500 transition-colors"
                          min="0"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">
                          {form.type === "percentage" ? "%" : currency.symbol}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Min Purchase Amount
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">
                          {currency.symbol}
                        </span>
                        <input
                          type="number"
                          placeholder="100"
                          value={form.minPurchaseAmount}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              minPurchaseAmount: Number(e.target.value),
                            })
                          }
                          className="w-full border-2 border-gray-200 pl-12 pr-4 py-3 rounded-xl focus:border-violet-500 transition-colors"
                          min="0"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Max Discount Amount
                      </label>
                      <div className="relative">
                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-semibold">
                          {currency.symbol}
                        </span>
                        <input
                          type="number"
                          placeholder="50"
                          value={form.maxDiscountAmount}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              maxDiscountAmount: Number(e.target.value),
                            })
                          }
                          className="w-full border-2 border-gray-200 pl-12 pr-4 py-3 rounded-xl focus:border-violet-500 transition-colors"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Usage Limits */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="w-6 h-6 text-violet-600" />
                    Usage Limits
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Total Usage Limit
                      </label>
                      <input
                        type="number"
                        placeholder="500"
                        value={form.usageLimit}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            usageLimit: Number(e.target.value),
                          })
                        }
                        className="w-full border-2 border-gray-200 px-4 py-3 rounded-xl focus:border-violet-500 transition-colors"
                        min="1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Usage Limit Per User
                      </label>
                      <input
                        type="number"
                        placeholder="1"
                        value={form.usageLimitPerUser}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            usageLimitPerUser: Number(e.target.value),
                          })
                        }
                        className="w-full border-2 border-gray-200 px-4 py-3 rounded-xl focus:border-violet-500 transition-colors"
                        min="1"
                      />
                    </div>

                    <div className="lg:col-span-2">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.applicableToNewUsersOnly}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              applicableToNewUsersOnly: e.target.checked,
                            })
                          }
                          className="w-8 h-8 rounded border-2 border-gray-300 text-violet-600 focus:ring-violet-500"
                        />
                        <span className="text-sm font-semibold text-gray-700">
                          Applicable to new users only
                        </span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Validity Period */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-6 h-6 text-violet-600" />
                    Validity Period
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={form.startsAt}
                        onChange={(e) =>
                          setForm({ ...form, startsAt: e.target.value })
                        }
                        className="w-full border-2 border-gray-200 px-4 py-3 rounded-xl focus:border-violet-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Expiry Date
                      </label>
                      <input
                        type="date"
                        value={form.expiresAt}
                        onChange={(e) =>
                          setForm({ ...form, expiresAt: e.target.value })
                        }
                        className="w-full border-2 border-gray-200 px-4 py-3 rounded-xl focus:border-violet-500 transition-colors"
                      />
                    </div>
                  </div>
                </div>

                {/* Internal Notes */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Info className="w-6 h-6 text-violet-600" />
                    Internal Notes
                  </h3>
                  <textarea
                    placeholder="Add any internal notes about this coupon (not visible to customers)"
                    value={form.internalNotes}
                    onChange={(e) =>
                      setForm({ ...form, internalNotes: e.target.value })
                    }
                    rows={3}
                    className="w-full border-2 border-gray-200 px-4 py-3 rounded-xl focus:border-violet-500 transition-colors resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-8 py-6 flex items-center justify-end gap-4 border-t border-gray-600">
              <button
                onClick={resetForm}
                className="px-6 py-3 rounded-xl font-semibold text-gray-700 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="btn-primary px-8 py-3 rounded-xl font-semibold text-white shadow-lg"
              >
                {editingCoupon ? "Update Coupon" : "Create Coupon"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Pagination indicators */}
      {apiTotalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={apiTotalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setItemsPerPage(size);
            setCurrentPage(1);
          }}
          pageSizeOptions={[5, 10, 25, 50, 100]}
          showPageSizeSelector={true}
          isLoading={loading}
          compact={true}
        />
      )}
    </div>
  );
};

export default CouponsPage;
